// src/services/keycloakService.js - Production ready service for user analytics
import { getData } from "../api/apiService";
import { trackUserCount, trackUserGrowth } from "../utils/googleAnalytics";
import { settings } from "../config/settings";

// Configuration
const CONFIG = {
  get REALM_NAME() {
    return settings.keycloakRealm;
  },
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000, // 2 seconds
  FETCH_INTERVAL: 5 * 60 * 1000, // 5 minutes
  USER_COUNT_ENDPOINT: "/admin/users/count",
  USER_LIST_ENDPOINT: "/admin/users",
};

// Store previous user count for growth tracking
let previousUserCount = null;
let isInitialized = false;

// Utility function for retry logic
const retryWithBackoff = async (fn, maxRetries = CONFIG.MAX_RETRIES) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }

      const delay = CONFIG.RETRY_DELAY * Math.pow(2, attempt - 1);
      console.warn(
        "Attempt %s failed, retrying in %sms...",
        attempt,
        delay,
        error.message,
      );

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

// Validate API response data
const validateUserCountData = (data) => {
  if (!data || typeof data.user_count !== "number") {
    throw new Error("Invalid user count data received from API");
  }

  if (data.user_count < 0) {
    throw new Error("User count cannot be negative");
  }

  return data.user_count;
};

const validateUserListData = (data) => {
  if (!data || !Array.isArray(data.users)) {
    throw new Error("Invalid user list data received from API");
  }

  return data.users;
};

// Main user count fetching function
export const fetchUserCount = async () => {
  console.log("fetchUserCount function called");

  try {
    console.log(
      `Making API request to ${CONFIG.USER_COUNT_ENDPOINT} using apiClient`,
    );

    // Use retry logic for production resilience
    const data = await retryWithBackoff(async () => {
      return await getData(CONFIG.USER_COUNT_ENDPOINT);
    });

    // Validate the response data
    const userCount = validateUserCountData(data);

    console.log("Data received successfully:", data);
    console.log("User count:", userCount);

    // Track user count in Google Analytics
    try {
      trackUserCount(CONFIG.REALM_NAME, userCount);
      console.log("User count sent to Google Analytics successfully");
    } catch (gaError) {
      console.warn("Google Analytics tracking failed:", gaError.message);
      // Don't fail the main function if GA fails
    }

    // Track user growth if we have previous data
    if (previousUserCount !== null && previousUserCount !== userCount) {
      try {
        trackUserGrowth(CONFIG.REALM_NAME, previousUserCount, userCount);
        console.log("User growth tracked successfully");
      } catch (gaError) {
        console.warn("User growth tracking failed:", gaError.message);
      }
    }

    // Update previous count for next comparison
    previousUserCount = userCount;
    isInitialized = true;

    return userCount;
  } catch (error) {
    console.error("Error in fetchUserCount:", error);

    // Log additional error context for production debugging
    if (error.response) {
      console.error("API Response Error:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      });
    }

    return null;
  }
};

// User list fetching function
export const fetchUserList = async (maxUsers = 20) => {
  console.log("fetchUserList function called");

  try {
    // Validate maxUsers parameter
    if (maxUsers < 1 || maxUsers > 1000) {
      throw new Error("maxUsers must be between 1 and 1000");
    }

    console.log(
      `Making API request to ${CONFIG.USER_LIST_ENDPOINT}?max=${maxUsers} using apiClient`,
    );

    // Use retry logic for production resilience
    const data = await retryWithBackoff(async () => {
      return await getData(`${CONFIG.USER_LIST_ENDPOINT}?max=${maxUsers}`);
    });

    // Validate the response data
    const users = validateUserListData(data);

    console.log("User list received successfully:", data);
    console.log(`Retrieved ${users.length} users`);

    // Track user list fetch in Google Analytics
    try {
      if (globalThis.gtag) {
        globalThis.gtag("event", "user_list_fetch", {
          event_category: "keycloak_metrics",
          event_label: "user_list_retrieved",
          value: users.length,
          custom_parameter_1: `${CONFIG.REALM_NAME}_users_list`,
          custom_parameter_2: maxUsers,
        });

        console.log("User list data sent to Google Analytics");
      }
    } catch (gaError) {
      console.warn("Google Analytics tracking failed:", gaError.message);
    }

    return users;
  } catch (error) {
    console.error("Error in fetchUserList:", error);
    return [];
  }
};

// Get service status
export const getServiceStatus = () => {
  return {
    isInitialized,
    previousUserCount,
    realmName: CONFIG.REALM_NAME,
    lastFetchTime: previousUserCount === null ? null : new Date().toISOString(),
  };
};

// Reset service state (useful for testing)
export const resetService = () => {
  previousUserCount = null;
  isInitialized = false;
  console.log("Service state reset");
};
